/**
 * Monitoring rule sets — V113-1-3 / 1-4 / 1-5 / 1-6 (ADR-0101 §2–§4, §6; migration 804).
 *
 * The content behind system-type inference, monitoring prescriptions, the repo-scan vocabulary and
 * detector candidates is versioned, reviewed content on the program_rule_sets pattern (ADR-0058):
 * one approved version per `family`, written only by `applyMonitoringRuleSetChange` from a
 * `monitoring_rule_set` proposal (zod schema per family, version = previous + 1), never edited.
 * Readers get the parsed, validated rules of the approved version — or null before one exists,
 * in which case inference, prescriptions and candidates compute nothing (no code defaults).
 *
 * Planes: monitoring_rule_sets is control plane; every caller passes the content database.
 */
import { z } from 'zod';
import { canonicalJson, sha256Hex } from '../utils/audit-crypto.js';
import { ContentValidationError } from './framework-requirement-service.js';
import { DETECTOR_KINDS, METHOD_FITS } from './monitoring-method-service.js';
import { ROLLUP_FIELDS, ROLLUP_KINDS } from './usage-rollup-service.js';
/**
 * `anomaly_rule` (ADR-0102 §2, Track 2) and `drift_rule` (ADR-0102 §7, Track 3) since migration 810;
 * `pack_material_change` (V113-5-1, ADR-0103 §2) since 825; `fulfillment_brief` (V114-1-3, ADR-0104 §3) since 856;
 * `gap_finding_sla` and `webhook_retry` (V114-6-2, ADR-0106) as code here; their database side arrives
 * with migration 864 (D-114-17: the family CHECK becomes the `monitoring_rule_set_families` lookup
 * table), held for release sequencing — until then no row of either family can exist and the services'
 * hard-coded fallbacks stand.
 */
export const MONITORING_RULE_FAMILIES = ['system_class', 'monitoring_prescription', 'scan_vocabulary', 'detector_candidate', 'anomaly_rule', 'drift_rule', 'pack_material_change', 'fulfillment_brief', 'gap_finding_sla', 'webhook_retry'];
export const SYSTEM_CLASSES = [
    'chatbot', 'agent', 'rag', 'fine_tuned_model', 'embedded_ml',
    'agentic_systems', 'ai_agents', 'llm_services', 'ml_models', 'data_pipelines',
];
export const isSystemClass = (v) => typeof v === 'string' && SYSTEM_CLASSES.includes(v);
const CONTROL_FAMILIES = ['agentic_systems', 'ai_agents', 'llm_services', 'ml_models', 'data_pipelines'];
const CONTROL_ID = /^[A-Z]{2,3}-\d{1,2}$/;
const KEY = /^[a-z][a-z0-9_]{1,63}$/;
const token = z.string().trim().min(1).max(120);
// ---- system_class ---------------------------------------------------------------------------
export const INFERENCE_SOURCES = ['repo_scan', 'infra', 'bom', 'observed'];
const SystemClassSignal = z.object({
    source: z.enum(INFERENCE_SOURCES),
    /** repo_scan: a Blueprint slot or an onboarding indicator prefix (`openai`, `model card`). */
    slot_any: z.array(token).min(1).max(20).optional(),
    indicator_any: z.array(token).min(1).max(20).optional(),
    /** repo_scan: a scan-vocabulary key (806). */
    vocab_any: z.array(z.string().regex(KEY)).min(1).max(20).optional(),
    /** infra: an inventory item's provider / product, or a clue of a kind whose value starts with one of the prefixes. */
    provider_key_any: z.array(token).min(1).max(20).optional(),
    product_any: z.array(token).min(1).max(20).optional(),
    clue: z.enum(['env_var', 'iac_resource', 'sdk', 'domain']).optional(),
    value_prefix_any: z.array(token).min(1).max(20).optional(),
    /** bom: an `ai_role`; observed: an `AiInventoryItem` kind. */
    ai_role_any: z.array(token).min(1).max(20).optional(),
    kind_any: z.array(token).min(1).max(20).optional(),
}).strict();
export const SystemClassRulesV1 = z.object({
    schema: z.literal('gacpro.monitoring-rules.system_class/1'),
    families: z.record(z.enum(SYSTEM_CLASSES), z.array(z.enum(CONTROL_FAMILIES)).min(1).max(5)),
    specificity: z.array(z.enum(SYSTEM_CLASSES)).min(1).max(10),
    fallback_classes: z.array(z.enum(SYSTEM_CLASSES)).max(10).default([]),
    thresholds: z.object({ strong: z.number().min(0).max(1), drift: z.number().min(0).max(1), auto_set: z.number().min(0).max(1) }).strict(),
    rules: z.array(z.object({
        id: z.string().regex(/^[a-z0-9][a-z0-9-]{1,63}$/),
        class: z.enum(SYSTEM_CLASSES),
        weight: z.number().gt(0).max(1),
        signal: SystemClassSignal,
    }).strict()).min(1).max(200),
}).strict();
// ---- monitoring_prescription ----------------------------------------------------------------
const DETECTOR_REF = new RegExp(`^(${DETECTOR_KINDS.join('|')}):[A-Za-z0-9][A-Za-z0-9_.:-]{0,118}$`);
export const PrescriptionControl = z.object({
    control_id: z.string().regex(CONTROL_ID),
    /** `<detector_kind>:<ref>` — a connector check key, a CI parser format, an aggregate rule id, a spec id, a library key. */
    detectors: z.array(z.string().regex(DETECTOR_REF)).min(1).max(12),
    /** The CI kinds the control is judged on (AND over them, ADR-0100 §4); empty = the app's own CI. */
    relevant_ci_kinds: z.array(token).max(12).default([]),
    cadence_seconds: z.number().int().positive().optional(),
}).strict();
export const MonitoringPrescriptionRulesV1 = z.object({
    schema: z.literal('gacpro.monitoring-rules.monitoring_prescription/1'),
    default_cadence_seconds: z.number().int().positive(),
    /**
     * D-113-28: the grace window (days) a newly discovered configuration-item asset stays `pending review` —
     * shown with its candidates, not counted as a cell — before it counts strictly. Content, not code;
     * absent (every rule set before D-113-28) → 14. 0 turns the grace off.
     */
    pending_review_days: z.number().int().min(0).max(90).default(14),
    classes: z.record(z.enum(SYSTEM_CLASSES), z.array(PrescriptionControl).min(1).max(60)),
}).strict();
// ---- scan_vocabulary --------------------------------------------------------------------------
export const ScanVocabularyEntry = z.object({
    key: z.string().regex(KEY),
    slot: z.string().regex(KEY),
    /** Package names as they appear in requirements / pyproject / package.json (names only, v10.7). */
    packages: z.array(z.string().trim().min(1).max(120)).min(1).max(20),
    detector_kind: z.enum(DETECTOR_KINDS),
    detector_ref: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9_.:-]{0,118}$/),
}).strict();
export const ScanVocabularyRulesV1 = z.object({
    schema: z.literal('gacpro.monitoring-rules.scan_vocabulary/1'),
    entries: z.array(ScanVocabularyEntry).min(1).max(200),
}).strict();
// ---- detector_candidate -------------------------------------------------------------------------
export const CANDIDATE_SOURCES = ['repo_scan', 'infra_inventory', 'observed_resource', 'intake_egress'];
export const DetectorCandidateRule = z.object({
    id: z.string().regex(/^[a-z0-9][a-z0-9-]{1,63}$/),
    source: z.enum(CANDIDATE_SOURCES),
    match: z.object({
        vocab_any: z.array(z.string().regex(KEY)).min(1).max(20).optional(),
        slot_any: z.array(z.string().regex(KEY)).min(1).max(20).optional(),
        provider_key: token.optional(),
        /** `*` = any product of the provider. */
        product: token.optional(),
        resource_kind_any: z.array(token).min(1).max(20).optional(),
    }).strict(),
    control_id: z.string().regex(CONTROL_ID),
    detector_kind: z.enum(DETECTOR_KINDS),
    detector_ref: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9_.:-]{0,118}$/),
    /** Default by source when absent: observed → native, repo → partial, intake/egress → substitute, infra → native with a connector of `connector_kind`, else substitute. */
    fit: z.enum(METHOD_FITS).optional(),
    connector_kind: z.string().regex(KEY).optional(),
    ci_placement: z.enum(['app', 'resource']),
    /** `resource` placement: the CI kinds the method lands on (default: any CI of the matched provider). */
    resource_kinds: z.array(token).max(12).optional(),
}).strict();
export const DetectorCandidateRulesV1 = z.object({
    schema: z.literal('gacpro.monitoring-rules.detector_candidate/1'),
    rules: z.array(DetectorCandidateRule).min(1).max(500),
}).strict();
// ---- anomaly_rule (ADR-0102 §2–§3) -----------------------------------------------------------------
const ANOMALY_RULE_ID = /^[a-z][a-z0-9_]{1,63}$/;
const rollupKinds = z.array(z.enum(ROLLUP_KINDS)).min(1).max(4);
const rollupField = z.enum(ROLLUP_FIELDS);
export const AnomalyMeasure = z.discriminatedUnion('type', [
    /** Daily sum of a column or metric over the rule's rollup kinds. */
    z.object({ type: z.literal('sum'), field: rollupField, rollup_kinds: rollupKinds }).strict(),
    /** Daily Σ numerator / Σ denominator; `min_volume` applies to the denominator. */
    z.object({ type: z.literal('ratio'), numerator: rollupField, denominator: rollupField, rollup_kinds: rollupKinds }).strict(),
    /** Daily maximum of a metric against `absolute_threshold` (no baseline needed). */
    z.object({ type: z.literal('max'), field: rollupField, rollup_kinds: rollupKinds }).strict(),
    /** A model carrying ≥ `min_share` of the day's `weight_field` that no baseline day had. */
    z.object({ type: z.literal('new_category'), category: z.literal('model'), weight_field: rollupField, rollup_kinds: rollupKinds }).strict(),
]);
export const AnomalyRule = z.object({
    id: z.string().regex(ANOMALY_RULE_ID),
    title: z.string().trim().min(3).max(120),
    control_id: z.string().regex(CONTROL_ID),
    severity: z.enum(['critical', 'high', 'medium', 'low']),
    measure: AnomalyMeasure,
    direction: z.enum(['up', 'down', 'both']),
    z_threshold: z.number().gt(0).max(100),
    min_relative_change: z.number().min(0).max(100),
    min_volume: z.number().min(0),
    absolute_threshold: z.number().min(0).optional(),
    min_share: z.number().gt(0).max(1).optional(),
}).strict().superRefine((r, ctx) => {
    if (r.measure.type === 'max' && r.absolute_threshold === undefined)
        ctx.addIssue({ code: 'custom', message: 'a max rule needs absolute_threshold', path: ['absolute_threshold'] });
    if (r.measure.type === 'new_category' && r.min_share === undefined)
        ctx.addIssue({ code: 'custom', message: 'a new_category rule needs min_share', path: ['min_share'] });
});
export const AnomalyRulesV1 = z.object({
    schema: z.literal('gacpro.monitoring-rules.anomaly_rule/1'),
    /** Complete UTC days before the evaluated day that form the baseline. */
    baseline_days: z.number().int().min(7).max(90),
    /** Days with rows needed in the window before a rule may open or close anything. */
    min_history_days: z.number().int().min(3).max(90),
    evaluate: z.literal('complete_days'),
    rules: z.array(AnomalyRule).min(1).max(50),
}).strict().superRefine((s, ctx) => {
    if (s.min_history_days > s.baseline_days)
        ctx.addIssue({ code: 'custom', message: 'min_history_days must be ≤ baseline_days', path: ['min_history_days'] });
    const ids = new Set();
    for (const r of s.rules) {
        if (ids.has(r.id))
            ctx.addIssue({ code: 'custom', message: `duplicate rule id ${r.id}`, path: ['rules'] });
        ids.add(r.id);
    }
});
// ---- drift_rule (ADR-0102 §7; Track 3 fills it) ---------------------------------------------------
export const DriftRulesV1 = z.object({
    schema: z.literal('gacpro.monitoring-rules.drift_rule/1'),
    /** Check keys whose details-hash change is a `config_change` audit entry (guardrail / filter / IAM binding). */
    config_change_check_keys: z.array(z.string().regex(KEY)).min(1).max(200),
    /** Detail keys left out of the details hash (timestamps, counts, run ids). */
    volatile_detail_keys: z.array(z.string().trim().min(1).max(64)).max(100).default([]),
    /** `control_degradation` severity; absent → the failing control's finding severity. */
    degradation_severity: z.enum(['critical', 'high', 'medium', 'low']).optional(),
}).strict();
// ---- pack_material_change (ADR-0103 §2; migration 826) ----------------------------------------------
/**
 * The reason codes the pack-validity derivation knows how to detect. The list (content) picks which apply,
 * their effect and thresholds; whether a reason clears by itself or needs a re-signed pack is a property of
 * the code (ADR-0103 §3, `PACK_REASON_CLEARS` in pack-validity-service), never content.
 */
export const PACK_MATERIAL_CHANGE_CODES = [
    'new_system_without_authority', 'new_system_not_in_pack', 'authority_drift', 'control_degradation',
    'new_unmonitored_applicable_risk', 'new_applicable_risk_not_in_pack', 'incident_above_threshold',
    'monitoring_evidence_stale', 'monitoring_testing_overdue', 'collector_unhealthy', 'ci_gone',
    // D-113-28 review H2: a configuration item taken out of scope while its bound check fails (built-in stale reason).
    'ci_out_of_scope_failing',
];
export const PackMaterialChange = z.object({
    code: z.enum(PACK_MATERIAL_CHANGE_CODES),
    effect: z.enum(['stale', 'invalidated']),
    /** control_degradation / incident_above_threshold / authority_drift: only at or above this severity. */
    min_severity: z.enum(['critical', 'high', 'medium', 'low']).optional(),
    /** incident_above_threshold: also when the recorded loss reaches this amount (cents). */
    min_loss_cents: z.number().int().nonnegative().optional(),
    /** monitoring_evidence_stale: AI machine determinations older than this are stale evidence. */
    max_age_hours: z.number().int().positive().max(24 * 90).optional(),
}).strict();
export const PackMaterialChangeRulesV1 = z.object({
    schema: z.literal('gacpro.monitoring-rules.pack_material_change/1'),
    changes: z.array(PackMaterialChange).min(1).max(PACK_MATERIAL_CHANGE_CODES.length)
        .refine((list) => new Set(list.map((c) => c.code)).size === list.length, { message: 'each code appears once' }),
}).strict();
// ---- fulfillment_brief (ADR-0104 §3; migration 856) --------------------------------------------------
/**
 * One reviewed brief template per GAP CLASS (`<family>:<reason>`, the fulfillment-gap-service key
 * parts). `what` / `why` may carry the context tokens {control_id} {component_name} {system_id}
 * {reason} {enabler_code}; the renderer substitutes them deterministically and NEVER invents an
 * option — a gap class with no template renders the generic family brief (fulfillment-brief-service).
 * Template seeds are Track 6 content (review queue, `source_kind='admin_edit'`).
 */
export const FULFILLMENT_OPTION_KINDS = ['in_app', 'templated', 'bespoke', 'admin_console'];
export const FulfillmentBriefOptionTemplate = z.object({
    kind: z.enum(FULFILLMENT_OPTION_KINDS),
    title: z.string().trim().min(3).max(120),
    steps: z.array(z.string().trim().min(1).max(300)).max(10).default([]),
    /** `templated`: the `gac fulfill` generator ref (Track 3); `bespoke`: the skill ref. */
    ref: z.string().trim().min(1).max(200).optional(),
}).strict();
export const FulfillmentBriefTemplate = z.object({
    gap_class: z.string().regex(/^(monitoring|check_failure|enabler|release):[a-z_]{1,64}$/),
    what: z.string().trim().min(3).max(600),
    why: z.string().trim().min(3).max(600),
    effort_min: z.number().int().min(1).max(240),
    /** Ranked: the order here is the order shown. */
    options: z.array(FulfillmentBriefOptionTemplate).min(1).max(6),
}).strict();
export const FulfillmentBriefRulesV1 = z.object({
    schema: z.literal('gacpro.monitoring-rules.fulfillment_brief/1'),
    templates: z.array(FulfillmentBriefTemplate).min(1).max(100)
        .refine((list) => new Set(list.map((t) => t.gap_class)).size === list.length, { message: 'each gap class appears once' }),
}).strict();
// ---- gap_finding_sla (ADR-0106 §1; V114-6-2, migration 883) -----------------------------------------
/**
 * Severity (and with it the SLA clock, `finding-service` SLA_HOURS) per gap finding kind. The kind
 * list mirrors GAP_FINDING_KINDS in gap-finding-service (inlined here to keep this module import-free
 * of it; a test pins the two lists together). `gapFindingSeverities` reads the approved row tolerantly
 * and falls back to GAP_FINDING_SEVERITY_FALLBACK until one exists.
 */
const gapFindingSla = z.object({ severity: z.enum(['critical', 'high', 'medium', 'low']) }).strict();
export const GapFindingSlaRulesV1 = z.object({
    schema: z.literal('gacpro.monitoring-rules.gap_finding_sla/1'),
    /** Per kind, each optional: an absent kind keeps its hard-coded fallback. */
    kinds: z.object({
        unmonitored_configuration_item: gapFindingSla.optional(),
        unmatched_control: gapFindingSla.optional(),
    }).strict(),
}).strict();
// ---- webhook_retry (ADR-0106 §2; V114-6-2, migration 884) -------------------------------------------
/**
 * The outbound-webhook retry policy (webhook-delivery-service). Bounds mirror the clamps
 * `webhookRetryPolicy` applies on read; the hard-coded WEBHOOK_RETRY_FALLBACK stands until a row
 * is approved.
 */
export const WebhookRetryRulesV1 = z.object({
    schema: z.literal('gacpro.monitoring-rules.webhook_retry/1'),
    max_attempts: z.number().int().min(1).max(20),
    base_delay_minutes: z.number().int().min(1).max(24 * 60),
    factor: z.number().min(1).max(10),
    cap_minutes: z.number().int().min(1).max(7 * 24 * 60),
}).strict();
const FAMILY_SCHEMAS = Object.freeze({
    system_class: SystemClassRulesV1,
    monitoring_prescription: MonitoringPrescriptionRulesV1,
    scan_vocabulary: ScanVocabularyRulesV1,
    detector_candidate: DetectorCandidateRulesV1,
    anomaly_rule: AnomalyRulesV1,
    drift_rule: DriftRulesV1,
    pack_material_change: PackMaterialChangeRulesV1,
    fulfillment_brief: FulfillmentBriefRulesV1,
    gap_finding_sla: GapFindingSlaRulesV1,
    webhook_retry: WebhookRetryRulesV1,
});
export const monitoringRuleSetIdFor = (family, version) => `${family}@v${version}`;
const str = (v) => (typeof v === 'string' ? v.trim() : '');
/**
 * Queue applier for `monitoring_rule_set` proposals: the family's zod schema, version = previous + 1,
 * `target_id` = `<family>@v<n>`; the previous approved version is superseded in the same batch.
 */
export async function applyMonitoringRuleSetChange(db, change, reviewer) {
    if (!reviewer)
        throw new ContentValidationError('monitoring_rule_set content needs a reviewer (provenance is mandatory)');
    if (!change.source_kind)
        throw new ContentValidationError('monitoring_rule_set content needs a source_kind');
    if (change.change_type !== 'create')
        throw new ContentValidationError('a monitoring rule set is never edited or deprecated; propose the next version');
    const raw = typeof change.proposed_state === 'string' ? JSON.parse(change.proposed_state) : change.proposed_state;
    if (!raw || typeof raw !== 'object' || Array.isArray(raw))
        throw new ContentValidationError('proposed_state must be an object');
    const p = raw;
    const family = str(p.family);
    if (!MONITORING_RULE_FAMILIES.includes(family))
        throw new ContentValidationError(`unknown monitoring rule family ${family || '(none)'}`);
    const effectiveFrom = str(p.effective_from);
    if (!/^\d{4}-\d{2}-\d{2}/.test(effectiveFrom))
        throw new ContentValidationError('effective_from must be a date (YYYY-MM-DD)');
    const rules = typeof p.rules_json === 'string' ? JSON.parse(p.rules_json) : p.rules_json;
    const parsed = FAMILY_SCHEMAS[family].safeParse(rules);
    if (!parsed.success) {
        const first = parsed.error.issues[0];
        throw new ContentValidationError(`rules_json is invalid: ${first?.path?.join('.') || '(root)'} ${first?.message ?? ''}`.trim());
    }
    const max = await db.query(`SELECT MAX(version) AS v FROM monitoring_rule_sets WHERE family = ?`, [family]);
    const next = Number(max.results[0]?.v ?? 0) + 1;
    if (p.version !== undefined && Number(p.version) !== next)
        throw new ContentValidationError(`version must be ${next} (the previous version + 1)`);
    const id = monitoringRuleSetIdFor(family, next);
    if (change.target_id && change.target_id !== id)
        throw new ContentValidationError(`target_id must be '${id}'`);
    const rulesJson = canonicalJson(parsed.data);
    const sha = await sha256Hex(rulesJson);
    const now = new Date().toISOString();
    await db.batch([
        { sql: `UPDATE monitoring_rule_sets SET status = 'superseded', superseded_at = ? WHERE family = ? AND status = 'approved'`, params: [now, family] },
        {
            sql: `INSERT INTO monitoring_rule_sets (rule_set_id, family, version, rules_json, content_sha256, source_version, effective_from, status, source_kind, reviewer, reviewed_at, change_id, created_at)
            VALUES (?, ?, ?, ?, ?, ?, ?, 'approved', ?, ?, ?, ?, ?)`,
            params: [id, family, next, rulesJson, sha, str(p.source_version) || null, effectiveFrom, change.source_kind, reviewer, change.reviewed_at ?? now, change.change_id, now],
        },
    ]);
    return id;
}
/** The approved rule set of a family, or null before one is approved (a row that fails its schema also reads null). */
export async function getCurrentMonitoringRuleSet(db, family) {
    const row = (await db.query(`SELECT * FROM monitoring_rule_sets WHERE family = ? AND status = 'approved' ORDER BY version DESC LIMIT 1`, [family])).results[0];
    if (!row)
        return null;
    let raw;
    try {
        raw = JSON.parse(row.rules_json);
    }
    catch {
        return null;
    }
    const parsed = FAMILY_SCHEMAS[family].safeParse(raw);
    return parsed.success ? { rules: parsed.data, version: row.version, rule_set_id: row.rule_set_id } : null;
}
export const getSystemClassRules = (db) => getCurrentMonitoringRuleSet(db, 'system_class');
export const getMonitoringPrescriptionRules = (db) => getCurrentMonitoringRuleSet(db, 'monitoring_prescription');
export const getScanVocabularyRules = (db) => getCurrentMonitoringRuleSet(db, 'scan_vocabulary');
export const getDetectorCandidateRules = (db) => getCurrentMonitoringRuleSet(db, 'detector_candidate');
export const getAnomalyRules = (db) => getCurrentMonitoringRuleSet(db, 'anomaly_rule');
export const getDriftRules = (db) => getCurrentMonitoringRuleSet(db, 'drift_rule');
export const getPackMaterialChangeRules = (db) => getCurrentMonitoringRuleSet(db, 'pack_material_change');
export const getFulfillmentBriefRules = (db) => getCurrentMonitoringRuleSet(db, 'fulfillment_brief');
export const getGapFindingSlaRules = (db) => getCurrentMonitoringRuleSet(db, 'gap_finding_sla');
export const getWebhookRetryRules = (db) => getCurrentMonitoringRuleSet(db, 'webhook_retry');
/** The prescribed controls for a class (empty for an unclassed system or an unknown class). */
export function prescriptionFor(rules, systemClass) {
    if (!rules || !isSystemClass(systemClass))
        return [];
    return rules.classes[systemClass] ?? [];
}
/** `monitoring_capable` (ADR-0101 §6): a product the detector-candidate rules can turn into a method. */
export function monitoringCapableProducts(rules) {
    const out = new Map();
    for (const r of rules?.rules ?? []) {
        if (r.source !== 'infra_inventory' || !r.match.provider_key)
            continue;
        const product = r.match.product ?? '*';
        out.set(`${r.match.provider_key}/${product}`, { provider_key: r.match.provider_key, product });
    }
    return [...out.values()].sort((a, b) => `${a.provider_key}/${a.product}`.localeCompare(`${b.provider_key}/${b.product}`));
}
//# sourceMappingURL=monitoring-rule-set-service.js.map